import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ShoppingCart, Star, Package, Bell, Trash2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Product } from '../../types';
import { resolvePrice } from '../../utils/price';
import { PRODUCT_BOX_DISCOUNTS } from '../../data/boxDiscount';
import { HoverPreview } from './HoverPreview';
import { ProductThumb } from './ProductThumb';
import { ColorBar } from './ColorBar';
import { OemCodesColumn } from './OemCodesColumn';
import { oemCodesCercatoPrima } from '../../utils/oemCodes';
import { CompatibilityChips, CompatModel, getCompatPreview } from './CompatibilityChips';
import { StockArrivalBadge } from './StockArrivalBadge';
import { RibassoBadge } from './RibassoBadge';
import { StockWarning } from './StockWarning';
import { BoxNudge } from './BoxNudge';
import { LoginToBuy } from './LoginToBuy';

interface ProductSearchRowProps {
  product: Product;
  isFirstRow: boolean;
  isLastRow: boolean;
  detailHref: string;
  isAuthenticated: boolean;
  compatModels?: CompatModel[];
  /** Modello attualmente ricercato (pagina "Consumabili per {modello}") —
   * se presente, l'anteprima compatibilità lo mostra come primo elemento
   * (vedi getCompatPreview). */
  searchedModel?: { id: string; nome: string; marca: string };
  /** Codice cercato nel catalogo (`?q=`): se colpisce un Codice OEM, quel
   * codice è il primo dell'elenco (vedi oemCodesCercatoPrima). */
  searchedCode?: string;
  isCompatExpanded: boolean;
  onToggleCompat: () => void;
  isOemExpanded: boolean;
  onToggleOem: () => void;
  onOpenKit: () => void;
  isFav: boolean;
  onToggleFavorite: () => void;
  alertActive: boolean;
  alertQty: number;
  onOpenAvvisami: () => void;
  // Riga "non ancora nel carrello" — stepper quantità + Acquista
  rowQty: number;
  onRowQtyChange: (qty: number) => void;
  onAddToCart: (e: React.MouseEvent) => void;
  showStockWarningInline: boolean;
  onOrderAvailableInline: () => void;
  onAddAvailableOnlyInline: () => void;
  // Riga "già nel carrello" — stepper quantità carrello
  cartQty: number;
  onUpdateCartQty: (qty: number) => void;
  showStockOptionsButton: boolean;
  showStockWarningCart: boolean;
  onToggleStockWarningCart: () => void;
  onOrderAvailableCart: () => void;
  onAddAvailableOnlyCart: () => void;
}

/** Riga della lista prodotti (Catalogo/Consumabili-per) — estratta da
 * `ProductSearchPage.tsx` per contenere le dimensioni del file (linee guida
 * <700 righe/componente). Nessuna modifica di comportamento: stessa
 * struttura JSX/data-testid, solo stato/callback ricevuti via props. */
export function ProductSearchRow({
  product: p, isFirstRow, isLastRow, detailHref, isAuthenticated,
  compatModels, searchedModel, searchedCode, isCompatExpanded, onToggleCompat, isOemExpanded, onToggleOem, onOpenKit,
  isFav, onToggleFavorite, alertActive, alertQty, onOpenAvvisami,
  rowQty, onRowQtyChange, onAddToCart, showStockWarningInline, onOrderAvailableInline, onAddAvailableOnlyInline,
  cartQty, onUpdateCartQty, showStockOptionsButton, showStockWarningCart, onToggleStockWarningCart, onOrderAvailableCart, onAddAvailableOnlyCart,
}: ProductSearchRowProps): React.ReactElement {
  const price = resolvePrice(p.sku, p.listino10, p.promo);
  const pStock = p.stock ?? 0;
  const outOfStock = pStock <= 0;
  const showInArrivo = outOfStock && Boolean(p.prossimArrivo);
  const { items: previewItems, total: compatTotal } = getCompatPreview(compatModels, p.compatibility, 3, false, searchedModel);
  const rawP = price ? parseFloat(price.current.replace('€', '').replace('.', '').replace(',', '.').trim()) : 0;
  const box = PRODUCT_BOX_DISCOUNTS[p.sku];
  const boxSconto = (() => {
    if (!box || !rawP) return 0;
    const cadBox = box.boxPrice / box.boxQty;
    return Math.round((1 - cadBox / rawP) * 100);
  })();

  return (
    <div className={`pt-3 sm:pt-4 lg:pt-5 px-3 sm:px-4 lg:px-5 pb-2 bg-white transition-colors border-b-2 border-slate-200 last:border-b-0 ${isFirstRow ? 'rounded-t-xl' : ''} ${isLastRow ? 'rounded-b-xl' : ''}`} data-testid="product-row">
      <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4 lg:gap-6">

        <div className="flex gap-3 sm:contents">
          {/* Colonna 1: Immagine */}
          <div className="relative flex-shrink-0 w-[86px] sm:w-[101px] lg:w-[115px]" data-testid={`product-col-image-${p.sku}`}>
            <HoverPreview sku={p.sku} category={p.category} title={p.title} brand={p.brand}>
              <Link to={detailHref}>
                <div className="relative w-full">
                  {p.lineaPromo && (
                    <div
                      className={`absolute top-0 left-0 z-10 text-white text-[8px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-br-lg rounded-tl-lg leading-tight ${p.lineaPromo === 'SMART' ? 'bg-sky-400' : 'bg-purple-600'}`}
                      data-testid={`promo-line-ribbon-${p.sku}`}
                    >
                      {p.lineaPromo}
                    </div>
                  )}
                  <ProductThumb sku={p.sku} brand={p.brand} color={p.color} category={p.category} size="lg" />
                  <ColorBar color={p.color} />
                </div>
              </Link>
            </HoverPreview>
          </div>

          {/* Colonna 2: Titolo + caratteristiche. Altezza FISSA (non max)
              pari a quella dell'immagine (104px su sm+, vedi ProductThumb
              size="lg"), così le chip di compatibilità (mt-auto) si
              allineano sempre al margine basso dell'immagine. La card nel
              suo insieme NON ha più un'altezza fissa (richiesta utente
              10/07/2026: "h-fit", niente spazio bianco per righe corte). */}
          <div className="flex-1 min-w-0 sm:min-w-[160px] sm:max-w-[300px] lg:max-w-[360px] sm:min-h-[104px] flex flex-col" data-testid={`product-col-info-${p.sku}`}>
            <div>
              <div className="flex items-start gap-2 mb-1">
                <div className="flex-1 min-w-0">
                  <Link to={detailHref} data-testid={`product-title-${p.sku}`}
                    className="text-sm font-semibold leading-snug text-[hsl(214,58%,41%)] underline-offset-2 hover:underline">
                    {p.title}
                  </Link>
                  {p.seedOnly && (
                    <span className="ml-1 text-[8px] uppercase tracking-wider font-bold px-1 py-0.5 rounded bg-orange-100 text-orange-700 border border-orange-200" data-testid={`demo-badge-${p.sku}`}>DEMO</span>
                  )}
                  {p.isKit && p.kitItems && p.kitItems.length > 0 && (
                    <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onOpenKit(); }} data-testid={`kit-badge-${p.sku}`}
                      className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-md bg-amber-50 border border-amber-200 text-amber-700 hover:bg-amber-100 transition-colors">
                      <Package className="w-3 h-3" /> Articoli del kit
                    </button>
                  )}
                </div>
              </div>
              <div className="text-xs text-muted-foreground">
                <span className="font-medium text-foreground">Cod. DTK</span> {p.sku}
              </div>
              {p.descrizione && (
                <p className="text-[11px] text-muted-foreground line-clamp-1 mt-0.5" data-testid={`product-descrizione-${p.sku}`} title={p.descrizione}>
                  {p.descrizione}
                </p>
              )}
            </div>
            {previewItems.length > 0 && (
              <div className="flex items-center gap-x-2.5 gap-y-1 flex-nowrap justify-start mt-auto pt-1.5" data-testid={`product-compat-preview-${p.sku}`}>
                {previewItems.map((it, i) => (
                  it.id ? (
                    <Link
                      key={i}
                      to={`/consumabili-per/${it.id}`}
                      onClick={e => e.stopPropagation()}
                      className="text-xs font-semibold text-[hsl(214,58%,41%)] underline decoration-1 decoration-[hsl(214,58%,41%)]/40 underline-offset-2 transition-colors flex-shrink-0 hover:text-[hsl(214,58%,46%)] hover:decoration-[hsl(214,58%,46%)] cursor-pointer"
                      title={`Cerca consumabili per ${it.label}`}
                      data-testid={`compat-preview-chip-link-${p.sku}`}
                    >
                      {it.label}
                    </Link>
                  ) : (
                    <span
                      key={i}
                      className="text-xs font-semibold text-[hsl(214,58%,41%)] underline decoration-1 decoration-[hsl(214,58%,41%)]/40 underline-offset-2 transition-colors flex-shrink-0"
                      title={it.label}
                    >
                      {it.label}
                    </span>
                  )
                ))}
                {compatTotal > 3 && (
                  <button
                    type="button"
                    onClick={e => { e.preventDefault(); e.stopPropagation(); onToggleCompat(); }}
                    data-testid="compat-toggle-btn"
                    title={isCompatExpanded ? 'Mostra meno' : 'Mostra tutte le compatibilità'}
                    className="flex-shrink-0 w-5 h-5 rounded-full border border-[hsl(214,58%,41%)]/30 text-[hsl(214,58%,45%)] hover:bg-[hsl(214,58%,41%)]/10 transition-colors flex items-center justify-center"
                  >
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${isCompatExpanded ? 'rotate-180' : ''}`} />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Colonna 3: Codici OEM (max 3, colonna singola, + espandi).
            Wrapper "flex-1 justify-center": occupa TUTTO lo spazio libero
            tra il blocco titolo (colonna 2) e il blocco prezzo/azioni
            (colonna 4, ancorato a destra), centrando il contenuto al suo
            interno — richiesta utente 23/02/2026: "sposta la colonna OEM
            al punto indicato, centrata tra lo spazio del titolo e quello
            dei bottoni". Se non ci sono codici OEM, il wrapper resta un
            semplice spaziatore invisibile (nessun impatto sul layout). */}
        <div className="flex-shrink-0 sm:flex-1 flex justify-center items-start sm:min-w-0" data-testid={`product-col-oem-${p.sku}`}>
          <OemCodesColumn
            codes={oemCodesCercatoPrima(p.oemCodes, searchedCode)}
            expanded={isOemExpanded}
            onToggle={onToggleOem}
          />
        </div>

        {/* Colonna 4: prezzo, stock, preferiti, acquista — bottoni subito
            sotto lo stato stock (non ancorati in fondo alla card, richiesta
            utente 10/07/2026). */}
        <div className="flex items-center sm:flex-col sm:items-end justify-between sm:justify-start flex-shrink-0 w-full sm:w-[190px] lg:w-[230px] gap-3 sm:gap-2 mt-1 sm:mt-0 sm:ml-auto" data-testid={`product-col-actions-${p.sku}`}>
          <div className="w-auto sm:w-full">
            {isAuthenticated && price ? (
              <div className="flex items-baseline gap-1.5 justify-end flex-wrap mb-1">
                {price.old && <span className="text-[13px] lg:text-xs line-through text-gray-600 tabular-nums">{price.old}</span>}
                <span className="text-xl font-black text-[hsl(214,58%,41%)] tabular-nums leading-none" data-testid={`price-${p.sku}`}>
                  {price.current.replace('€', '').trim()}<span className="text-xs font-semibold ml-0.5">€</span>
                </span>
              </div>
            ) : null}
            {isAuthenticated && p.ribasso && (
              <div className="flex justify-end mb-1">
                <RibassoBadge ribasso={p.ribasso} variant="compact" />
              </div>
            )}
            <div className="flex items-center gap-1.5 justify-end mb-2 flex-wrap">
              {p.webSellable === false ? (
                <>
                  {/* Non vendibile online: nessuna giacenza mostrata (INT-112). */}
                  <div className="w-2.5 h-2.5 rounded-full flex-shrink-0 bg-gray-400" data-testid={`stock-dot-${p.sku}`} />
                  <span className="text-[13px] lg:text-[10px] font-semibold text-gray-500">Non disponibile</span>
                </>
              ) : showInArrivo ? (
                <>
                  <div className="w-2.5 h-2.5 rounded-full flex-shrink-0 bg-amber-400" data-testid={`stock-dot-${p.sku}`} />
                  <StockArrivalBadge prossimArrivo={p.prossimArrivo!} qtaArrivo={p.qtaArrivo} variant="text" />
                </>
              ) : (
                <>
                  <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${outOfStock ? 'bg-red-500' : 'bg-emerald-500'}`} data-testid={`stock-dot-${p.sku}`} />
                  <span className={`text-[13px] lg:text-[10px] font-semibold ${outOfStock ? 'text-red-600' : 'text-emerald-600'}`}>
                    {outOfStock ? 'Esaurito' : 'Disponibile'}
                  </span>
                </>
              )}
            </div>
          </div>

          {isAuthenticated && box && boxSconto > 0 && (
            <div className="relative group/box flex items-center gap-1 justify-end cursor-help mb-1.5 w-full" data-testid={`box-discount-inline-${p.sku}`}>
              <span className="text-[10px] text-emerald-700 font-medium">Sconto scatola -{boxSconto}%</span>
              <span className="w-3.5 h-3.5 rounded-full bg-[hsl(214,58%,41%)] text-white text-[8px] font-bold flex items-center justify-center flex-shrink-0">i</span>
              <div className="absolute bottom-full right-0 mb-1.5 hidden group-hover/box:block z-50">
                <div className="bg-[hsl(214,58%,41%)] text-white rounded-lg p-3 shadow-lg text-[11px] leading-relaxed whitespace-nowrap">
                  <div>Prezzo listino: € {rawP.toFixed(2).replace('.', ',')}</div>
                  <div>Prezzo scontato: € {(box.boxPrice / box.boxQty).toFixed(2).replace('.', ',')}</div>
                  <div>Sconto scatola: -{boxSconto}%</div>
                  <div className="mt-1 pt-1 border-t border-white/30">Quantità Scatola: {box.boxQty}pz</div>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-end gap-2 w-full">
            {isAuthenticated && (
              <button
                onClick={onToggleFavorite}
                title={isFav ? 'Rimuovi dai preferiti' : 'Aggiungi ai preferiti'}
                className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 transition-all ${isFav ? 'bg-amber-400 text-white shadow-sm' : 'bg-amber-50 border border-amber-200 text-amber-400 hover:bg-amber-400 hover:text-white hover:border-amber-400'}`}
                data-testid={`fav-toggle-${p.sku}`}
              >
                <Star className={`w-3.5 h-3.5 ${isFav ? 'fill-white' : 'fill-amber-300'}`} />
              </button>
            )}

            <div className="min-w-0" data-testid={`action-block-${p.sku}`}>
              {p.webSellable === false ? (
                <span className="h-8 px-3 rounded-lg bg-gray-100 text-gray-500 border border-gray-200 text-[10px] font-medium flex items-center gap-1" data-testid={`not-sellable-${p.sku}`} title="Articolo non acquistabile online">
                  <AlertTriangle className="w-3 h-3" /> Non disponibile
                </span>
              ) : !isAuthenticated ? (
                <div className="flex justify-end w-full">
                  <LoginToBuy />
                </div>
              ) : outOfStock ? (
                alertActive ? (
                  <div className="flex justify-end w-full">
                    <button
                      onClick={onOpenAvvisami}
                      data-testid={`avviso-attivo-${p.sku}`}
                      title={`Avviso attivo per ${alertQty} pz — clicca per modificare`}
                      className="relative h-8 px-3 rounded-lg bg-amber-100 border border-amber-300 text-amber-800 text-[10px] font-semibold flex items-center gap-1.5 hover:bg-amber-200 transition-colors"
                    >
                      <Bell className="w-3.5 h-3.5" />
                      <span>Avviso attivo</span>
                      <span className="min-w-[18px] h-[18px] rounded-full bg-amber-500 text-white text-[9px] font-bold flex items-center justify-center px-1">
                        {alertQty}
                      </span>
                    </button>
                  </div>
                ) : (
                  <div className="flex justify-end w-full">
                    <button onClick={onOpenAvvisami} data-testid={`avvisami-${p.sku}`}
                      className="h-8 w-[110px] rounded-lg bg-[#8a1719] text-white text-[10px] font-semibold flex items-center justify-center gap-1.5 hover:bg-[#701315] transition-colors">
                      <Bell className="w-3.5 h-3.5" /> Avvisami
                    </button>
                  </div>
                )
              ) : cartQty === 0 ? (
                <div className="flex flex-col items-end gap-1.5 w-full">
                  <div className="flex items-center gap-1 w-full">
                    <div className="flex items-center border rounded-lg overflow-hidden h-8 flex-shrink-0">
                      <button onClick={() => onRowQtyChange(Math.max(1, rowQty - 1))} className="w-6 h-8 flex items-center justify-center text-muted-foreground hover:bg-secondary/60 transition-colors text-sm font-bold" aria-label="Riduci quantità" data-testid={`qty-minus-${p.sku}`}>−</button>
                      <input type="text" inputMode="numeric" pattern="[0-9]*" value={rowQty}
                        onChange={e => { const v = parseInt(e.target.value) || 1; onRowQtyChange(Math.max(1, v)); }}
                        onFocus={e => e.target.select()}
                        className="w-8 h-8 text-center text-xs font-bold bg-transparent focus:outline-none tabular-nums [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        data-testid={`qty-input-${p.sku}`} />
                      <button onClick={() => onRowQtyChange(Math.min(pStock, rowQty + 1))} className="w-6 h-8 flex items-center justify-center text-muted-foreground hover:bg-secondary/60 transition-colors text-sm font-bold" aria-label="Aumenta quantità" data-testid={`qty-plus-${p.sku}`}>+</button>
                    </div>
                    <button onClick={onAddToCart} data-testid={`add-to-cart-${p.sku}`}
                      className="flex-1 h-8 px-3 rounded-lg bg-[hsl(214,58%,41%)] text-white text-[10px] font-bold uppercase flex items-center justify-center gap-1 hover:bg-[hsl(214,58%,46%)] transition-colors tracking-wide">
                      <ShoppingCart className="w-3 h-3" /> Acquista
                    </button>
                  </div>
                  {showStockWarningInline && pStock > 0 && rowQty > pStock && (
                    <StockWarning
                      sku={p.sku}
                      qtyRequested={rowQty}
                      stockAvailable={pStock}
                      variant="inline"
                      onOrderAvailable={onOrderAvailableInline}
                      onAddAvailableOnly={onAddAvailableOnlyInline}
                    />
                  )}
                </div>
              ) : (
                <div className="relative flex flex-col items-end gap-0.5 w-full">
                  <div className={`flex items-center rounded-lg border-2 w-full ${cartQty > pStock && pStock > 0 ? 'border-amber-400 bg-amber-50' : 'border-emerald-300 bg-emerald-50'}`} data-testid={`cart-stepper-${p.sku}`}>
                    <button onClick={() => onUpdateCartQty(cartQty - 1)} className={`w-8 h-8 flex items-center justify-center rounded-l-md transition-colors ${cartQty > pStock && pStock > 0 ? 'text-amber-700 hover:bg-amber-100' : 'text-emerald-700 hover:bg-emerald-100'}`}>
                      {cartQty === 1 ? <Trash2 className="w-3 h-3 text-red-400" /> : <span className="text-sm font-bold">-</span>}
                    </button>
                    <input type="text" inputMode="numeric" pattern="[0-9]*" value={cartQty}
                      onChange={e => { const v = parseInt(e.target.value) || 0; if (v >= 0) onUpdateCartQty(v); }}
                      onFocus={e => e.target.select()}
                      className={`flex-1 h-8 text-center text-xs font-bold bg-transparent tabular-nums focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${cartQty > pStock && pStock > 0 ? 'text-amber-700' : 'text-emerald-700'}`} />
                    <button onClick={() => onUpdateCartQty(cartQty + 1)} className={`w-8 h-8 flex items-center justify-center rounded-r-md transition-colors ${cartQty > pStock && pStock > 0 ? 'text-amber-700 hover:bg-amber-100' : 'text-emerald-700 hover:bg-emerald-100'}`}>
                      <span className="text-sm font-bold">+</span>
                    </button>
                  </div>
                  {showStockOptionsButton && (
                    <button onClick={onToggleStockWarningCart} className="text-[8px] text-amber-600 font-medium hover:underline cursor-pointer">
                      Solo {pStock} disponibili — opzioni
                    </button>
                  )}
                  {showStockWarningCart && (
                    <StockWarning sku={p.sku} qtyRequested={cartQty} stockAvailable={pStock}
                      onOrderAvailable={onOrderAvailableCart}
                      onAddAvailableOnly={onAddAvailableOnlyCart}
                    />
                  )}
                  {rawP > 0 && (
                    <BoxNudge sku={p.sku} qty={cartQty} unitPrice={rawP} onSetQty={onUpdateCartQty} onBoxCompleted={(_boxes, bq) => toast.success(`Scatola da ${bq} aggiunta al carrello!`)} />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Compatibilità — sotto le 4 colonne. Visibilità pilotata dal bottone
          sotto "Cod. DTK" (stessa animazione già esistente: overflow-hidden +
          transition-[max-height], ora applicata all'intera sezione). */}
      {(compatModels?.length ?? 0) > 0 || p.compatibility ? (
        <div
          className="overflow-hidden transition-[max-height] duration-300 ease-in-out"
          style={{ maxHeight: isCompatExpanded ? '600px' : '0px' }}
          data-testid={`product-compat-row-${p.sku}`}
        >
          <div className="mt-3 pt-3 border-t border-slate-100">
            <CompatibilityChips
              modelli={compatModels}
              compatibility={p.compatibility}
              productTitle={p.title}
            />
          </div>
        </div>
      ) : null}

    </div>
  );
}
